/**
 * Where the prompt box is: docked along the bottom (where it opens, every time the app starts),
 * floating where the person dragged it, at the size they gave it, or closed. The menu bar's
 * "Prompt" puts it back docked. A floating box always stays inside the window.
 *
 * Or standing along the right edge, a column of the layout (owner, 10/10/2026; docs/refactor/29): the page makes
 * room for it, only its width changes, it still folds — and the choice is remembered on this computer.
 */
import { slug } from './composer'

export type DockPlace = 'docked' | 'floating' | 'closed' | 'side'

/** The right-hand column's width: never narrower than usable, never more than most of the window. */
export const SIDE_MIN_W = 320
export function sideWidth(w: number, viewWidth: number): number {
  return Math.round(Math.min(Math.max(w, SIDE_MIN_W), Math.max(SIDE_MIN_W, viewWidth * 0.6)))
}

export interface DockGeometry {
  x: number
  y: number
  w: number
  h: number
}

export const MIN_W = 360
export const MIN_H = 160
/** How much of a floating box must stay in the window, so its title bar can always be grabbed. */
const KEEP = 48

/** A floating box moved to (x, y): its title bar stays reachable. */
export function moveTo(geo: DockGeometry, x: number, y: number, view: { w: number; h: number }): DockGeometry {
  return {
    ...geo,
    x: Math.round(Math.min(Math.max(x, KEEP - geo.w), view.w - KEEP)),
    y: Math.round(Math.min(Math.max(y, 0), view.h - KEEP)),
  }
}

/** A floating box resized from its corner: never smaller than usable, never past the window. */
export function resizeTo(geo: DockGeometry, w: number, h: number, view: { w: number; h: number }): DockGeometry {
  return {
    ...geo,
    w: Math.round(Math.min(Math.max(w, MIN_W), Math.max(MIN_W, view.w - geo.x))),
    h: Math.round(Math.min(Math.max(h, MIN_H), Math.max(MIN_H, view.h - geo.y))),
  }
}

/** The event the menu bar's "Prompt" sends: the box comes back, docked and open. */
export const DOCK_EVENT = 'devpulse:prompt-dock'

export function redock(): void {
  window.dispatchEvent(new CustomEvent(DOCK_EVENT))
}

/** The event that opens the prompt box with a text to start from: "@casa-pc " from Suas máquinas. */
export const COMPOSE_EVENT = 'devpulse:prompt-compose'

/** Opens the prompt box with "@<machine> " in it, the cursor after it, ready for what to do there. */
export function composeFor(machineName: string): void {
  // The same name the composer's @ matches (lib/composer.ts).
  window.dispatchEvent(new CustomEvent(COMPOSE_EVENT, { detail: `@${slug(machineName)} ` }))
}

/** The event that sends a text through the prompt box as if typed and sent: "/testar" from Mudanças, Ctrl+K. */
export const SEND_EVENT = 'devpulse:prompt-send'

/**
 * Sends `text` through the prompt box — a /command or a prompt — so it goes where anything typed
 * goes (here on the desktop, to the computer at home from the web), in the conversation open there.
 */
export function sendCommand(text: string, shown?: string): void {
  window.dispatchEvent(new CustomEvent<SendDetail>(SEND_EVENT, { detail: { text, ...(shown ? { shown } : {}) } }))
}

/**
 * What a screen sends through the box. `shown`: the words the box and the answer show for it, when the text is
 * instructions built behind a button ("Deixe a sua IA guiar você") — the agent still gets the whole text.
 */
export interface SendDetail {
  text: string
  shown?: string
}

/** Opens the super skills (docs/refactor/50) in the prompt box: the menu, or one of them by key ("video"), from Ctrl+K. */
export const SUPER_SKILLS_EVENT = 'devpulse:super-skills'

export function openSuperSkills(key?: string): void {
  window.dispatchEvent(new CustomEvent<string | null>(SUPER_SKILLS_EVENT, { detail: key ?? null }))
}
